import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type { AuditPage, AuditUser } from '../api/monitor';
import { EMPTY_RANGE, presetRange, rangeBounds } from '../lib/audit';
import type { AuditRange, RangePresetId } from '../lib/audit';

export interface AuditController {
  page: AuditPage | null;
  /** Opções do filtro de usuário. */
  users: AuditUser[];
  /** `null` = todos os usuários. */
  userId: number | null;
  range: AuditRange;
  /** Atalho de período em vigor; nulo quando o período foi escolhido à mão. */
  preset: RangePresetId | null;
  /** Se algum filtro está ligado — é o que mostra o "Limpar filtros". */
  filtered: boolean;
  loading: boolean;
  error: string | null;
  setUser: (id: number | null) => void;
  setRange: (range: AuditRange) => void;
  applyPreset: (preset: RangePresetId, days: number) => void;
  clearFilters: () => void;
  goToPage: (page: number) => void;
  reload: () => void;
}

/**
 * Tela de Auditoria: o histórico paginado no servidor, com filtro de usuário e
 * de período.
 *
 * Os filtros vão para o servidor em vez de recortar uma lista já carregada
 * porque a tabela só cresce — auditoria não tem limpeza — e o recorte comum
 * ("o que o fulano fez ontem") pega poucas linhas de um histórico grande.
 */
export function useAudit(): AuditController {
  const [page, setPage] = useState<AuditPage | null>(null);
  const [users, setUsers] = useState<AuditUser[]>([]);
  const [userId, setUserId] = useState<number | null>(null);
  const [range, setRangeValue] = useState<AuditRange>(EMPTY_RANGE);
  const [preset, setPreset] = useState<RangePresetId | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Respostas voltam fora de ordem quando o filtro muda no meio do caminho; só
  // a última pedida pode escrever na tela.
  const latestRequest = useRef(0);

  const { from, to } = useMemo(() => rangeBounds(range), [range]);

  useEffect(() => {
    monitorApi
      .auditUsers()
      .then((response) => setUsers(response.users))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setLoading(true);

    monitorApi
      .audit({ userId, from, to, page: pageNumber })
      .then((response) => {
        if (latestRequest.current !== requestId) return;
        setPage(response);
        setError(null);
      })
      .catch((cause: Error) => {
        if (latestRequest.current !== requestId) return;
        setError(cause.message);
      })
      .finally(() => {
        if (latestRequest.current !== requestId) return;
        setLoading(false);
      });
  }, [userId, from, to, pageNumber, reloadToken]);

  const setUser = useCallback((id: number | null) => {
    setUserId(id);
    setPageNumber(1);
  }, []);

  // Mexer no calendário desliga o atalho: a pílula acesa passaria a prometer um
  // período que não é mais o que está no campo.
  const setRange = useCallback((next: AuditRange) => {
    setRangeValue(next);
    setPreset(null);
    setPageNumber(1);
  }, []);

  const applyPreset = useCallback((id: RangePresetId, days: number) => {
    setRangeValue(presetRange(days));
    setPreset(id);
    setPageNumber(1);
  }, []);

  const clearFilters = useCallback(() => {
    setUserId(null);
    setRangeValue(EMPTY_RANGE);
    setPreset(null);
    setPageNumber(1);
  }, []);

  const goToPage = useCallback((next: number) => setPageNumber(Math.max(1, next)), []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return {
    page,
    users,
    userId,
    range,
    preset,
    filtered: userId !== null || range.start !== '',
    loading,
    error,
    setUser,
    setRange,
    applyPreset,
    clearFilters,
    goToPage,
    reload,
  };
}
